/**
 * @author: lv.chen
 * @date: 2025/12/15
 * @desc: ProductChangedRankCommonMetric组件
 */
import React from 'react';
import classnames from 'classnames';
import GameImage from '@home/components/GameImage';
import EllipsisHover from '@home/components/EllipsisHover';
import NoData from '@intelligence/components/NoData';
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatPercentNum } from '@intelligence/v3/utils/number';
import { getCountryFlag } from '@home/_config/countries';
import { valueFormat } from '@intelligence/utils/commonUtils';
import { getRankElement, getDodClassName, renderChangeTag } from '../../utils';

import IMG_ICON_HUOYUE from '@intelligence/assets/imgs/casualDailyReports/icon-huoyue.png';

interface IProductChangedRankCommonMetricProps extends IBaseProps {
  rankTitle: string;
  rankSubTitle: string;
  // 无数据提示文案
  noDataText?: string;
  ranks?: ISensorTowerCommonMetricChangeRankItem[] | null;
  onGoToGameDetails?: (game?: any) => void;
  footer?: React.ReactNode | null;
}

const ProductChangedRankCommonMetric: React.FC<IProductChangedRankCommonMetricProps> = (props) => {
  const {
    style,
    className,
    rankTitle,
    rankSubTitle,
    noDataText = '暂无数据',
    ranks,
    onGoToGameDetails,
    footer,
  } = props;

  const isNoData = isEmpty(ranks);

  return (
    <div
      style={style}
      className={classnames(
        'dbi-casual-daily-reports-product-changed-rank-common-metric dbi-update-rank-card',
        className,
      )}
    >
      <div className="dbi-update-rank-card-content">
        <div className="dbi-update-rank-card-hd">
          <div className="dbi-update-rank-card-title">
            <img className="dbi-update-rank-card-title-img" src={IMG_ICON_HUOYUE} alt="" />
            <span>{rankTitle}</span>
          </div>
          <div className="dbi-update-rank-card-th">{rankSubTitle}</div>
          <div className="dbi-update-rank-card-th">
            变化量
            <span className="dbi-icon-rank-down"></span>
          </div>
        </div>
        <div className="dbi-update-rank-list">
          {isNoData && <NoData text={noDataText} />}
          {isNoData || (
            <>
              {ranks?.map((rank, rankIndex) => {
                const rankChangePct = rank?.change_pct;
                const rankChangePctFormatted = formatPercentNum(rankChangePct);
                const tag = rank?.tag;
                const tagDesc = rank?.tag_desc;

                return (
                  <div
                    key={rankIndex}
                    className="dbi-update-rank-item dbi-cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onGoToGameDetails?.(rank);
                    }}
                  >
                    <div className="dbi-rank-card">
                      <div className="dbi-rank-card-index">{getRankElement(rankIndex)}</div>
                      <div className="dbi-rank-card-info">
                        <div className="dbi-rank-card-info-avatar">
                          <GameImage entityType="mobile" src={rank?.cover} />
                        </div>
                        <div className="dbi-rank-card-info-text">
                          <div className="dbi-rank-card-info-title">
                            <EllipsisHover text={rank?.entity_name || ''} />
                          </div>
                          <div className="dbi-rank-card-info-desc">
                            <img
                              className="dbi-rank-card-info-desc-img"
                              src={getCountryFlag(rank?.publisher_country_abbre)}
                              alt="rank"
                            />
                            <EllipsisHover text={rank?.publisher} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="dbi-update-rank-card-td">
                      <span className="dbi-update-rank-text">{valueFormat(rank?.cur_value)}</span>
                      {rankChangePctFormatted && (
                        <span className={classnames('dbi-update-rank-change', getDodClassName(rankChangePct))}>
                          {rankChangePctFormatted}
                        </span>
                      )}
                    </div>
                    <div className="dbi-update-rank-card-td">
                      <span className="dbi-update-rank-text">{valueFormat(rank?.delta)}</span>
                      {renderChangeTag(tag, tagDesc)}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
        {footer}
      </div>
    </div>
  );
};

export default ProductChangedRankCommonMetric;
